import { Component, EventEmitter, Injector, OnInit, Output } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { GlobalSpinnerService } from 'src/app/shared/services/global-spinner.service';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { LoginCompanyPickerDialogComponent } from 'src/app/components/login-company-picker-dialog/login-company-picker-dialog.component';
import { EnvironmentService } from 'src/app/core/services/environment/environment.service';
import { RecentApp, ResourceLink, UserData, UserProfile, WizardUrl } from 'src/app/models';
import { AlertModel, AlertType } from 'src/app/models/alert.model';
import { AuthenticationUserCompany } from 'src/app/models/auth/authentication-response.model';
import { ExternalCompanyService } from 'src/app/modules/admin/external-company-management/services';
import { AlertsCreatedEvent, AlertsService } from 'src/app/services/alerts.service';
import { AuthService } from 'src/app/services/auth.service';
import { ChannelService } from 'src/app/services/channel.service';
import { ConfigurationService } from 'src/app/services/configuration.service';
import { Constants } from 'src/app/services/constants';
import { MentionEvent, MentionsService } from 'src/app/services/mentions.service';
import { NotificationService } from 'src/app/services/notification.service';
import { ApplicationContextBoundComponent } from 'src/app/shared/components';
import { copyToClipboard } from 'src/utils';
import { AlertBucket, Notifications } from '../../admin/header/header-actions/header-actions.component';

@Component({
    selector: 'tpo-header',
    templateUrl: './tpo-header.component.html',
    styleUrls: ['./tpo-header.component.scss'],
    standalone: false
})
export class TpoHeaderComponent extends ApplicationContextBoundComponent implements OnInit {
  @Output()
  notificationsClicked: EventEmitter<Notifications> = new EventEmitter<Notifications>();

  @Output()
  alertsCreated: EventEmitter<AlertModel[]> = new EventEmitter<AlertModel[]>();

  @Output()
  mentionsClicked: EventEmitter<boolean> = new EventEmitter<boolean>();

  currentlyLoggedInUser: UserData;
  currentlyLoggedInUserProfile: UserProfile;
  recentApps: Array<RecentApp> = [];
  resourceLinks: Array<ResourceLink> = [];
  companyLogoUrl: string = "";
  userAvatarUrl: string;
  isNewLeadReferralEnabled: boolean;
  isNewApplicationEnabled: boolean;
  companies: AuthenticationUserCompany[] = [];
  wizardUrls: WizardUrl[] = [];
  currentUserIsPrimaryRoleUser: boolean = false;
  isApprisalDisabled: boolean = false;

  isNewClicked: boolean = false;
  isSupportTicketingEnabled: boolean = false;
  brokerSupportPageTitle: string = 'Broker Support';

  alertsAndNotifications: Notifications = new Notifications();
  unreadMentionCount: number = 0;
  version: string = '';

  protected isCompanyDeepHaven: boolean = false;
  protected isCompanySmartfi: boolean = false;

  protected isOnlineAppEnabled: boolean = true;

  private _contextChangesSubscription: Subscription;
  private _activatedRouteEventsSubscription: Subscription;
  private _mentionsSubscription: Subscription;
  private _alertsSubscription: Subscription;
  private _loanInfoChangesSubscription: Subscription;
  private _routerEventsSubscription: Subscription;

  constructor(private readonly injector: Injector,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _router: Router,
    private readonly _authService: AuthService,
    private readonly _spinner: GlobalSpinnerService,
    private readonly _environmentService: EnvironmentService,
    private readonly _mentionsService: MentionsService,
    private readonly _alertsService: AlertsService,
    private readonly _modalService: NgbModal,
    private readonly _notifyService: NotificationService,
    private readonly _channelService: ChannelService,
    private readonly _externalCompanyService: ExternalCompanyService,
    private readonly _configurationService: ConfigurationService
  ) {
    super(injector);
    this.version = this._environmentService.version;
    this.isCompanyDeepHaven = this.applicationContext.isCompanyDeepHaven;
    this.isCompanySmartfi = this.applicationContext.isCompanySmartfi;
    this._contextChangesSubscription = this.applicationContextService.changes.subscribe(context => {
      if (context) {
        this.currentlyLoggedInUser = context.currentlyLoggedInUser;
        this.currentlyLoggedInUserProfile = context.currentlyLoggedInUserProfile.userProfile;
        this.recentApps = context.userPermissions.recentApplications;
        this.brokerSupportPageTitle = context.tpoConfig?.brokerSupportConfig?.pageTitle || 'Broker Support';
        this.rebuildPrmgResourceLinks();
      }
    });

    this._loanInfoChangesSubscription = this.applicationContextService.loanInfoChanges.subscribe(event => {
      if (event?.context) {
        this.rebuildPrmgResourceLinks();
      }
    });

    this._routerEventsSubscription = this._router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => {
        this.rebuildPrmgResourceLinks();
      });

    this._alertsSubscription = this._alertsService.events.subscribe((event: AlertsCreatedEvent) => {
      if (!event) {
        return;
      }
      this.alertsCreated.emit(event.alerts);
    });

    this._mentionsSubscription = this._mentionsService.events.subscribe((event: MentionEvent) => {
      if (!event) return;
      if (event.type === "reload") {
        setTimeout(this.getUnreadMentionsCount, 1000);
      }
    })

    this.alertsAndNotifications = this.initializeAlertsAndNotifications();
  }

  ngOnInit(): void {
    this.companies = this.applicationContext.currentlyLoggedInUser.allUserCompanyData || [];
    this._activatedRouteEventsSubscription = this._activatedRoute.data.subscribe((data) => {
      this.companyLogoUrl = this._environmentService.apiInfo.apiBaseUrl + `company/br/logo/${this.applicationContext.userPermissions.companyId}`;
      this.currentlyLoggedInUser = this.applicationContext.currentlyLoggedInUser;
      this.currentlyLoggedInUserProfile = this.applicationContext.currentlyLoggedInUserProfile.userProfile;
      this.wizardUrls = this.applicationContext.userPermissions.onlineApplications?.wizardUrls.filter(x => x.isDefault) ?? [];
      this.currentUserIsPrimaryRoleUser = true; // Hack for demo this.applicationContext.globalConfig.firstRole?.roleId == this.applicationContext.userPermissions.roleId;
      this.isNewApplicationEnabled = this.applicationContext.userPermissions.showNewSubmissionForm;
      this.isNewLeadReferralEnabled = this.applicationContext.userPermissions.companyId == 170; // for loda and solar
      this.isOnlineAppEnabled = this.applicationContext.userPermissions.onlineApplicationEnabled;

      if (!this.currentlyLoggedInUser.avatar) {
        this.userAvatarUrl = 'assets/images/male.png';
      } else {
        this.userAvatarUrl = this.currentlyLoggedInUser.avatar;
      }
      // ?? can we look for loan Id here maybe?
    });

    this.recentApps = this.applicationContext.userPermissions.recentApplications;
    this.brokerSupportPageTitle = this.applicationContext.tpoConfig?.brokerSupportConfig?.pageTitle || 'Broker Support';
    this.resourceLinks = new Array<ResourceLink>();

    if (this.applicationContext.isCompanyPRMG) {
      this.rebuildPrmgResourceLinks();
    } else if (this.applicationContext.isCompanyOpenWholesale) {
      this.resourceLinks.push({ displayText: "Resource Center", url: "https://www.openwholesale.com/resource-center" });
      this.resourceLinks.push({ displayText: "DreamBuilder Resource Center", url: "https://www.openwholesale.com/dreambuilder" });
    } else if (this.applicationContext.isCompanyDoorway) {
      this.resourceLinks.push({ displayText: "Resource Center", url: "https://www.doorwaywholesale.com/resource-center" });
      this.resourceLinks.push({ displayText: "DreamBuilder Resource Center", url: "https://www.doorwaywholesale.com/dreambuilder" });
    } else if (this.applicationContext.isCompanyDeepHaven) {
      this.resourceLinks.push({ displayText: "Resource Center", url: "https://deephavenmortgage.com/thehavenwholesaleresources/" });
    }
    else if (this.applicationContext.isCompanyArdri) {
      this.resourceLinks.push({ displayText: "TPO Resource Center", url: "https://ardri.ai/ardrihubtporesourcecenter2495114/" });
    } else if (this.applicationContext.isCompanyPlainsCommerce) {
      this.resourceLinks.push({ displayText: "Order Appraisal", url: "https://plainscommercebank.vmpclient.com/SignIn.aspx" });
      this.resourceLinks.push({ displayText: "Appraisal Deposit", url: "https://paymentsolutions.plainscommerce.com/?page=search_appraisal_deposit" });
    } else if (this.applicationContext.isCompanyRoundHouse) {
      this.resourceLinks.push({ displayText: "User Guides | RoundHouse Lending", url: "https://www.roundhouselending.com/resources-user-guides" });
    } else if (this.applicationContext.isCompanyPerpetual) {
      this.resourceLinks.push({ displayText: "Elektrikai Login", url: "https://elektrik-ai.com/askelektrik/" });
      this.resourceLinks.push({ displayText: "Resources", url: "https://rockstartpo.com/broker-resources-training/" });
      this.resourceLinks.push({ displayText: "Submit a Scenario / Exception Form", url: "https://rockstartpo.com/scenario-exception-submission/" });
      this.resourceLinks.push({ displayText: "Submit an Appraisal / CDA Order", url: "https://rockstartpo.com/order-an-appraisal/" });
    }

    if (this.applicationContext && this.applicationContext.userPermissions.enabledChannels !== "Disabled") {
      const enabledChannels = this._channelService.getChannelsFromCommaDelimitedString(this.applicationContext.userPermissions.enabledChannels);
      this.isApprisalDisabled = enabledChannels.some(e => ["Correspondent", "NonDelegatedCorrespondent"].includes(e.name));
    }

    if (this.applicationContext.userPermissions.externalCompanyId) {
      this._externalCompanyService.getCurrentTpoExternalCompany().subscribe((company) => {
        if (company) {
          const features = company.f as any;
          this.isOnlineAppEnabled = features.onlineApplicationEnabled;
        }
      });
    }

    this._configurationService.getCompanyConfiguration('SupportTicketingEnabled').subscribe({
      next: config => {
        this.isSupportTicketingEnabled = config?.value === 1;
      },
      error: () => {
        this.isSupportTicketingEnabled = false;
      }
    });

    this.getAlerts([]);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._contextChangesSubscription?.unsubscribe();
    this._activatedRouteEventsSubscription?.unsubscribe();
    this._mentionsSubscription?.unsubscribe();
    this._alertsSubscription?.unsubscribe();
    this._loanInfoChangesSubscription?.unsubscribe();
    this._routerEventsSubscription?.unsubscribe();
  }

  onSwitchCompanyClicked = () => {
    const modalRef = this._modalService.open(
      LoginCompanyPickerDialogComponent,
      Constants.modalOptions.medium
    );
    const updatedCompanies = this.companies.map(company => ({
      ...company,
      externalCompanyName: company.externalCompanyId
        ? this.getExternalCompanyNameById(company.externalCompanyId)
        : null
    }));
    modalRef.componentInstance.availableCompanies = updatedCompanies;
  };

  copyLink = (wizardUrl: WizardUrl) => {
    copyToClipboard(wizardUrl.url);
    this._notifyService.showInfo("Link copied to clipboard", "Info");
  }

  onLogoutClicked = () => {
    this._spinner.show();
    this._authService.logout().subscribe(() => {
      this._spinner.hide();
    }, error => {
      this._spinner.hide();
    });
  };

  onNewClicked = () => {
    this.isNewClicked = !this.isNewClicked;
  }

  onNewApplicationClicked = () => {
    this.isNewClicked = false;
  }

  onNewLeadReferralClicked = () => {
    this.isNewClicked = false;
  }

  showNotifications() {
    this.notificationsClicked.emit(this.alertsAndNotifications);
  }

  showMentions() {
    this.mentionsClicked.emit(true);
  }

  hasPpeAccess(): boolean {
    if (!this.applicationContext.isCompanyPRMG) {
      return true;
    }

    const ppeChannels = this.applicationContext.userPermissions.ppeLoanChannels;
    return ppeChannels && ppeChannels.length > 0;
  }

  private getExternalCompanyNameById(externalCompanyId: number) {
    const matchingCompany = this.applicationContext?.globalConfig?.externalCompanies.find(ec => ec.externalCompanyId === externalCompanyId);
    return matchingCompany?.name || '';
  }

  private initializeAlertsAndNotifications = (): Notifications => {
    const alertsAndNotifications = new Notifications();

    const borrowerMessagesBucket = new AlertBucket("Borrower Messages", AlertType.BorrowerMessageAlert, 1501);
    const taskStatusChangesBucket = new AlertBucket("Task Status Changes", AlertType.TaskStatusAlert, 1502);
    const notificationsBucket = new AlertBucket("Misc Notifications", AlertType.GeneralAlert, 1503);
    const loanStatusChangesBucket = new AlertBucket("Loan Status Changes", AlertType.LoanStatusAlert, 1504);
    const emailAlertsBucket = new AlertBucket("Failed E-mails", AlertType.FailedEmailAlert, 1505);
    const smsAlertsBucket = new AlertBucket("Failed SMS", AlertType.FailedSmsAlert, 1506);
    const newLeadAlertsBucket = new AlertBucket("New Leads", AlertType.NewLeadAlert, 1507);
    const borrowerPrequalBucket = new AlertBucket("Borrower Generated Prequal", AlertType.BorrowerGeneratedPrequal, 1509);
    const agentPrequalBucket = new AlertBucket("Agent Generated Prequal", AlertType.AgentGeneratedPrequal, 1510);
    const ocrSuccessBucket = new AlertBucket("OCR Completed Success", AlertType.OCRCompleted_Success, 1511);
    const OcrFailureBucket = new AlertBucket("OCR Completed Failure", AlertType.OCRCompleted_Failure, 1512);
    const TridAlertBucket = new AlertBucket("TRID Triggers", AlertType.TridHasBeenTriggered, 1513);
    const voicemailsBucket = new AlertBucket("Voicemails", AlertType.VoicemailAlert, 1515);

    alertsAndNotifications.alertBuckets.push(newLeadAlertsBucket);
    alertsAndNotifications.alertBuckets.push(notificationsBucket);
    alertsAndNotifications.alertBuckets.push(loanStatusChangesBucket);
    alertsAndNotifications.alertBuckets.push(taskStatusChangesBucket);
    alertsAndNotifications.alertBuckets.push(borrowerPrequalBucket);
    alertsAndNotifications.alertBuckets.push(agentPrequalBucket);
    alertsAndNotifications.alertBuckets.push(ocrSuccessBucket);
    alertsAndNotifications.alertBuckets.push(OcrFailureBucket);
    alertsAndNotifications.alertBuckets.push(borrowerMessagesBucket); //TODO: remove me when conversations works fully
    alertsAndNotifications.alertBuckets.push(emailAlertsBucket);
    alertsAndNotifications.alertBuckets.push(smsAlertsBucket);
    alertsAndNotifications.alertBuckets.push(TridAlertBucket);
    alertsAndNotifications.alertBuckets.push(voicemailsBucket);

    return alertsAndNotifications;
  }

  private rebuildPrmgResourceLinks(): void {
    if (!this.applicationContext.isCompanyPRMG) {
      return;
    }

    const links = this.getPrmgCommonLinks();

    const channel = this.applicationContext.application?.channel;
    if (channel) {
      if (channel === 'Wholesale') {
        links.push(...this.getPrmgWholesaleLinks())
      } else if (channel === 'Correspondent' || channel === 'NonDelegatedCorrespondent') {
        links.push(...this.getPrmgCorrespondentLinks())
      } else {
        links.push(...this.getPrmgWholesaleLinks())
        links.push(...this.getPrmgCorrespondentLinks())
      }
    } else {
      const enabledChannels = this._channelService
        .getChannelsFromCommaDelimitedString(this.applicationContext.userPermissions.enabledChannels)
        .map(c => c.name);

      if (enabledChannels.includes('Wholesale')) {
        links.push(...this.getPrmgWholesaleLinks());
      }

      if (enabledChannels.includes('Correspondent') || enabledChannels.includes('NonDelegatedCorrespondent')) {
        links.push(...this.getPrmgCorrespondentLinks());
      }
    }

    this.resourceLinks = links;
  }

  private getPrmgCommonLinks(): ResourceLink[] {
    return [
      { displayText: 'Micro Training Videos', url: 'https://eprmg.net/Training/Videos/LEOMicroLearningVideos.html' },
      { displayText: 'Training Materials & Webinar Link', url: 'https://eprmg.net/Training/Videos/LEO.html' },
      { displayText: 'VA Payment', routerLink: '/tpo/va-payment' },
    ];
  }

  private getPrmgWholesaleLinks(): ResourceLink[] {
    return [
      { displayText: 'WS Turn Times', url: 'https://eprmg.net/ResourceCenter/TurnTimes/WholesaleTurnTimes.pdf' },
      { displayText: 'WS Resource Center', url: 'https://www.eprmg.net/ResourceCenter/WholesaleRC.pdf' },
      { displayText: 'WS Product Profiles', url: 'https://www.eprmg.net/guidelines/PRMGProductProfileLinksWholesale.pdf' },
      { displayText: 'Request System Access', url: 'https://www.prmg.net/wholesale-online-sign-up-request/' },
      { displayText: 'Subscribe to Rate Sheet', url: 'https://www.prmg.net/rate-sheet-form/' },
    ];
  }

  private getPrmgCorrespondentLinks(): ResourceLink[] {
    return [
      { displayText: 'Corr Turn Times', url: 'https://eprmg.net/ResourceCenter/TurnTimes/CorrTurnTimes.pdf' },
      { displayText: 'Correspondent Resource Center', url: 'https://www.eprmg.net/ResourceCenter/CorrespondentRC.pdf' },
      { displayText: 'Correspondent Product Profiles', url: 'https://www.eprmg.net/guidelines/PRMGProductProfileLinksCorrespondent.pdf' },
      { displayText: 'Delegated Contact and Tip Sheet', url: 'https://www.eprmg.net/ResourceCenter/PoliciesProceduresInformation/DelegatedCorrespondentContactandTipSheet.pdf' },
      { displayText: 'NDC Contact and Tip Sheet', url: 'https://www.eprmg.net/ResourceCenter/PoliciesProceduresInformation/Non-DelegatedCorrespondentContactandTipSheet.PDF' },
    ];
  }

  private getAlerts(data) {
    this.getUnreadMentionsCount();
    this.fillAlertsBuckets(this.alertsAndNotifications);
  }

  private getUnreadMentionsCount = () => {
    this._mentionsService.getMentionsCount().subscribe(counts => {
      this.unreadMentionCount = counts.unreadMentionCount ?? 0;
    });
  }

  private fillAlertsBuckets = (notifications: Notifications) => {
    const apiCalls: Observable<any>[] = [];
    notifications.alertBuckets.forEach(b => {
      const apiCall = this._alertsService.getAlertsByType(b.typeId);
      apiCalls.push(apiCall);
    });

    if (apiCalls.length) {
      forkJoin(apiCalls).subscribe({
        next: (results) => {
          results.forEach((alerts: AlertModel[], index) => {
            const bucketToFill = notifications.alertBuckets[index];
            if (bucketToFill.type === AlertType.FailedEmailAlert || bucketToFill.type === AlertType.FailedSmsAlert) {
              alerts = alerts.filter((a: AlertModel) => !a.isRead)
            }
            bucketToFill.alerts = alerts;
          })
        }
      });
    }
  }

}


